/**
 * Market-wide sell-through, worked out from BrickLink's price guide.
 *
 * "Sold" is the quantity sold by every seller on BrickLink over the last 6 months. "For sale" is the quantity
 * listed right now by every seller. Both are the price guide's `total_quantity`: pieces, not lots.
 *
 *   sell-through    = sold / (sold + for sale)
 *   months of stock = for sale / (sold / 6)         how long today's listings last at the last 6 months' pace
 *
 * Pure and browser-safe: the API, the worker and the web app all use this one definition.
 */

/** BrickLink's "sold" price guide covers the last 6 months. */
export const SOLD_WINDOW_MONTHS = 6;

export const PRICE_GUIDE_REGIONS = ['worldwide', 'uk', 'europe'] as const;
export type PriceGuideRegion = (typeof PRICE_GUIDE_REGIONS)[number];
export const DEFAULT_PRICE_GUIDE_REGION: PriceGuideRegion = 'worldwide';

export const PRICE_GUIDE_REGION_LABEL: Record<PriceGuideRegion, string> = {
  worldwide: 'Worldwide',
  uk: 'United Kingdom',
  europe: 'Europe',
};

export type PriceGuideType = 'sold' | 'stock';

/**
 * What the figures say about a lot.
 *
 *   ok               it sold, and some is for sale
 *   none_for_sale    it sold, and nobody has any listed: 100% sell-through, no months of stock
 *   no_recent_sales  some is for sale, but none sold in 6 months: 0% sell-through, never runs out
 *   no_market        none sold and none for sale: there is nothing to measure
 */
export type SellThroughState = 'ok' | 'none_for_sale' | 'no_recent_sales' | 'no_market';

export interface SellThrough {
  state: SellThroughState;
  /** Pieces sold on BrickLink in the last 6 months. */
  sold: number;
  /** Pieces for sale on BrickLink now. */
  forSale: number;
  /** 0 to 1. Null only when nothing was sold and nothing is for sale. */
  sellThrough: number | null;
  /** Pieces sold per month on average over the window. */
  soldPerMonth: number;
  /**
   * Months the listings last at that pace. Null when it cannot be said: either nothing sold
   * (see `state`, which says "no recent sales" or "no market"), so it would be infinite.
   */
  monthsOfStock: number | null;
}

const whole = (n: number, what: string) => {
  if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) throw new RangeError(`${what} must be a whole number of zero or more`);
  return n;
};

/** Works out the measure from a sold quantity and a for-sale quantity. Both must be whole numbers of zero or more. */
export function sellThrough(sold: number, forSale: number): SellThrough {
  whole(sold, 'sold');
  whole(forSale, 'forSale');
  const soldPerMonth = sold / SOLD_WINDOW_MONTHS;

  if (sold === 0) {
    return forSale === 0
      ? { state: 'no_market', sold, forSale, sellThrough: null, soldPerMonth, monthsOfStock: null }
      : { state: 'no_recent_sales', sold, forSale, sellThrough: 0, soldPerMonth, monthsOfStock: null };
  }
  return {
    state: forSale === 0 ? 'none_for_sale' : 'ok',
    sold,
    forSale,
    sellThrough: sold / (sold + forSale),
    soldPerMonth,
    monthsOfStock: forSale / soldPerMonth,
  };
}

/** Plain-English months, for people: "under a month", "3.5 months", "2 years", "no recent sales". */
export function describeMonthsOfStock(s: SellThrough): string {
  if (s.state === 'no_market') return 'nothing sold or listed';
  if (s.state === 'no_recent_sales') return 'no recent sales';
  const m = s.monthsOfStock ?? 0;
  if (s.state === 'none_for_sale') return 'none for sale';
  if (m < 1) return 'under a month';
  if (m < 10) return `${Math.round(m * 10) / 10} months`;
  if (m < 24) return `${Math.round(m)} months`;
  return `${Math.round((m / 12) * 10) / 10} years`;
}

/** "46%" or "—". */
export function describeSellThrough(s: SellThrough): string {
  if (s.sellThrough === null) return '—';
  return `${Math.round(s.sellThrough * 100)}%`;
}

/** The one-line explanation shown beside the numbers. */
export function explainSellThrough(s: SellThrough): string {
  const per = s.soldPerMonth >= 10 ? Math.round(s.soldPerMonth) : Math.round(s.soldPerMonth * 10) / 10;
  switch (s.state) {
    case 'no_market':
      return 'None sold in the last 6 months and none for sale on BrickLink.';
    case 'no_recent_sales':
      return `${s.forSale.toLocaleString('en-GB')} for sale on BrickLink, but none sold in the last 6 months.`;
    case 'none_for_sale':
      return `${s.sold.toLocaleString('en-GB')} sold in the last 6 months (about ${per} a month) and none for sale on BrickLink.`;
    case 'ok':
      return `${s.sold.toLocaleString('en-GB')} sold in the last 6 months (about ${per} a month) against ${s.forSale.toLocaleString('en-GB')} for sale: ${describeSellThrough(s)} sell-through, ${describeMonthsOfStock(s)} of stock.`;
  }
}

// ---------------------------------------------------------------- how fresh the numbers are

/** Older than this, the figures are shown as out of date. */
export const MARKET_STALE_DAYS = 45;

/** "just now", "3 days ago", "2 months ago". */
export function describeAge(fetchedAt: Date | string | null | undefined, now: Date = new Date()): string {
  if (!fetchedAt) return 'never';
  const ms = now.getTime() - new Date(fetchedAt).getTime();
  const days = Math.floor(ms / 86_400_000);
  if (ms < 0 || days < 0) return 'just now';
  if (days === 0) return ms < 3_600_000 ? 'just now' : 'today';
  if (days === 1) return 'yesterday';
  if (days < 60) return `${days} days ago`;
  return `${Math.round(days / 30)} months ago`;
}

// ---------------------------------------------------------------- what the inventory shows for a lot

/** One cached price guide, as the API returns it. Prices are decimal strings in `currency`. */
export interface MarketGuide {
  /** Pieces. */
  quantity: number;
  /** Lots (listings, or sales). Null when BrickLink did not say. */
  lots: number | null;
  currency: string | null;
  min: string | null;
  avg: string | null;
  qtyAvg: string | null;
  max: string | null;
  fetchedAt: string;
}

/** A cache row as it comes out of the database for one lot, before it is made into a LotMarket. */
export interface MarketCacheRow {
  guide: PriceGuideType;
  status: 'ok' | 'unavailable';
  quantity: number | null;
  lots: number | null;
  currency: string | null;
  min: string | null;
  avg: string | null;
  qtyAvg: string | null;
  max: string | null;
  note: string | null;
  at: string;
}

/**
 * The market figures for one lot, from the cache.
 *
 *   ready        both guides are in: `measure` is set
 *   pending      only one is in; the other will follow on a later run
 *   unavailable  BrickLink has no price guide for this lot
 *
 * A lot with nothing cached has no LotMarket at all (null).
 */
export interface LotMarket {
  region: PriceGuideRegion;
  status: 'ready' | 'pending' | 'unavailable';
  /** When the older of the two guides was fetched. */
  fetchedAt: string;
  sold: MarketGuide | null;
  forSale: MarketGuide | null;
  measure: SellThrough | null;
  note: string | null;
}

const guideOf = (r: MarketCacheRow): MarketGuide | null =>
  r.status === 'ok' && r.quantity !== null
    ? { quantity: r.quantity, lots: r.lots, currency: r.currency, min: r.min, avg: r.avg, qtyAvg: r.qtyAvg, max: r.max, fetchedAt: r.at }
    : null;

export function buildMarket(rows: MarketCacheRow[] | null | undefined, region: PriceGuideRegion): LotMarket | null {
  if (!rows || rows.length === 0) return null;
  const soldRow = rows.find((r) => r.guide === 'sold');
  const stockRow = rows.find((r) => r.guide === 'stock');
  const fetchedAt = rows.map((r) => r.at).sort()[0]!;

  const unavailable = rows.find((r) => r.status === 'unavailable');
  if (unavailable) {
    return { region, status: 'unavailable', fetchedAt, sold: null, forSale: null, measure: null, note: unavailable.note };
  }
  const sold = soldRow ? guideOf(soldRow) : null;
  const forSale = stockRow ? guideOf(stockRow) : null;
  if (sold && forSale) {
    return { region, status: 'ready', fetchedAt, sold, forSale, measure: sellThrough(sold.quantity, forSale.quantity), note: null };
  }
  return { region, status: 'pending', fetchedAt, sold, forSale, measure: null, note: null };
}

// ---------------------------------------------------------------- exports

/** The market columns of a CSV or JSON export. Only lots with both guides cached have figures. */
export interface ExportMarket {
  region: PriceGuideRegion;
  soldLast6Months: number;
  forSale: number;
  /** 0 to 100, one decimal. Null when nothing was sold and nothing is for sale. */
  sellThroughPercent: number | null;
  /** Null when it cannot be said: see noRecentSales. */
  monthsOfStock: number | null;
  /** Some is for sale but none sold in 6 months, so the months of stock are endless. */
  noRecentSales: boolean;
  /** When the older of the two guides was fetched. */
  fetchedAt: string;
}

export function exportMarketOf(m: LotMarket | null): ExportMarket | null {
  if (!m || m.status !== 'ready' || !m.measure) return null;
  const x = m.measure;
  return {
    region: m.region,
    soldLast6Months: x.sold,
    forSale: x.forSale,
    sellThroughPercent: x.sellThrough === null ? null : Math.round(x.sellThrough * 1000) / 10,
    monthsOfStock: x.monthsOfStock === null ? null : Math.round(x.monthsOfStock * 10) / 10,
    noRecentSales: x.state === 'no_recent_sales',
    fetchedAt: m.fetchedAt,
  };
}
